import { PRODUCT_NAME } from "@staffsmith/web-auth";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "../context.js";
import { ErrorNote } from "../ui.js";

// Sign in with an emailed one-time code (Q20) or a password. The code flow has
// two steps: ask for a code, then enter it. Password is the default until SES
// leaves its sandbox (QUESTIONS.md Q24); until then codes reach only verified
// addresses and anyone else would wait for an email that never comes.

type Mode =
  { kind: "code" } | { kind: "code-sent"; email: string; session: string } | { kind: "password" };

export function SignInPage() {
  const { auth } = useServices();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>({ kind: "password" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const attempt = async (step: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const switchTo = (next: Mode) => {
    setError(null);
    setMode(next);
  };

  return (
    <main className="sign-in">
      <h1>{PRODUCT_NAME}</h1>
      {mode.kind === "code" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const email = String(new FormData(e.currentTarget).get("email"));
            void attempt(async () => {
              const session = await auth.startEmailCode(email);
              setMode({ kind: "code-sent", email, session });
            });
          }}
        >
          <label>
            Email
            <input name="email" type="email" autoComplete="username" required />
          </label>
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Sending…" : "Email me a sign-in code"}
          </button>
          <button type="button" className="link" onClick={() => switchTo({ kind: "password" })}>
            Use a password instead
          </button>
        </form>
      ) : mode.kind === "code-sent" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const code = String(new FormData(e.currentTarget).get("code")).trim();
            void attempt(async () => {
              await auth.confirmEmailCode(mode.email, mode.session, code);
              void navigate("/decisions");
            });
          }}
        >
          <p className="muted">We emailed a code to {mode.email}. It expires in a few minutes.</p>
          <label>
            Code
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6,8}"
              required
            />
          </label>
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button type="button" className="link" onClick={() => switchTo({ kind: "code" })}>
            Start again
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            void attempt(async () => {
              await auth.signInWithPassword(
                String(form.get("email")),
                String(form.get("password")),
              );
              void navigate("/decisions");
            });
          }}
        >
          <label>
            Email
            <input name="email" type="email" autoComplete="username" required />
          </label>
          <label>
            Password
            <input name="password" type="password" autoComplete="current-password" required />
          </label>
          <ErrorNote error={error} />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button type="button" className="link" onClick={() => switchTo({ kind: "code" })}>
            Email me a code instead
          </button>
        </form>
      )}
    </main>
  );
}
